import { CommandBar, ConstrainMode, IColumn, IGroup, DetailsListLayoutMode, DetailsRow, ICommandBarItemProps, IDetailsColumnRenderTooltipProps, IDetailsHeaderProps, IDetailsListProps, IDetailsRowStyles, IRenderFunction, IShimmeredDetailsListProps, Selection, mergeStyleSets, ScrollablePane, ScrollbarVisibility, SelectionMode, ShimmeredDetailsList, Stack, Sticky, StickyPositionType, TooltipHost } from '@fluentui/react';
import React from 'react';
import appstyles from '../styles/App.module.scss';
import useColumnFilters, { ColumnFilterDefinitions, IColumnFilterDefinition } from '../hooks/useColumnFilters';
import ColumnFilterMenu from './ColumnFilterMenu';

/** Kolonne med filter i overskriften. true filtrerer på værdien i fieldName */
export interface IFilterableColumn extends IColumn
{
    filter?: true | IColumnFilterDefinition<any>;
}

interface IFreezableDetailsListProps extends IShimmeredDetailsListProps
{
    onSelectionChanged: (item: any) => void;
    enableClickRowOpen?: boolean;
    /** Kaldes med de viste rækker, når kolonnefiltrene ændrer dem */
    onFilteredItemsChanged?: (items: any[] | undefined) => void;
    /** Bygger grupperne ud fra de viste rækker, så de passer efter søgning og filtrering. Bruges i stedet for groups */
    getGroups?: (items: any[]) => IGroup[];
}

export const FreezableDetailsList: React.FunctionComponent<IFreezableDetailsListProps> = (props: React.PropsWithChildren<IFreezableDetailsListProps>) => {
    const [selected, setSelected] = React.useState<any | undefined>(undefined);

    const filterDefinitions: ColumnFilterDefinitions<any> = {};
    (props.columns as IFilterableColumn[] | undefined)?.forEach(column => {
      if(column.filter === true) {
        filterDefinitions[column.key] = { getValue: (item: any) => item[column.fieldName ?? column.key] };
      } else if(column.filter) {
        filterDefinitions[column.key] = column.filter;
      }
    });
    const { filteredItems, applyToColumns, filterMenuProps } = useColumnFilters<any>(props.items, filterDefinitions);

    React.useEffect(() => {
      selection.setAllSelected(false);
    }, [filteredItems]);

    React.useEffect(() => {
      props.onFilteredItemsChanged?.(filteredItems);
    }, [filteredItems]);

    // Kun genberegnet når rækkerne ændres, så foldede/udfoldede grupper bevares mellem renders
    const groups = React.useMemo(() : IGroup[] | undefined => {
      return props.getGroups && filteredItems ? props.getGroups(filteredItems) : props.groups;
    }, [filteredItems, props.groups]);

    const scrollClass = mergeStyleSets({
          detailsListWrapper: {
              height: '100%',
              width: '100%',
              position: 'relative'
          },
    });

    const _renderFixedDetailsHeader: IRenderFunction<IDetailsHeaderProps> = (props, defaultRender) => {
          if (!props) {
              return null;
          }
          const onRenderColumnHeaderTooltip: IRenderFunction<IDetailsColumnRenderTooltipProps> =
              tooltipHostProps => (
                  <TooltipHost {...tooltipHostProps} />
              );
          return (
              <Sticky stickyPosition={StickyPositionType.Header} isScrollSynced>
                  {defaultRender!({
                      ...props,
                      onRenderColumnHeaderTooltip,
                  })}
              </Sticky>
          );
      }

      const _onRenderRow: IDetailsListProps['onRenderRow'] = prop => {
          const customStyles: Partial<IDetailsRowStyles> = {};
          if (prop) {
            if(props.selectionMode !== SelectionMode.none)
            {                
                return <div title={props.enableClickRowOpen ? "Klik for at åbne" : ""} style={{cursor: "pointer"}} onClick={() => { props.onSelectionChanged(prop.item); }}><DetailsRow {...prop} styles={customStyles} /></div>
            } else
            {
                return <DetailsRow {...prop} styles={customStyles} />
            }
          }
          return null;
        }

        const selection = React.useMemo(() => new Selection({
                onSelectionChanged: () => {       
                  const selectedItems = selection.getSelection() 
        
                  if(selectedItems.length == 1)
                  {
                    const currentSelection = selection.getSelection()[0];            
                    setSelected(currentSelection);
                    props.onSelectionChanged(currentSelection);
                  } else
                  {
                    setSelected(undefined);
                    props.onSelectionChanged(undefined);
                  }
                }
              }), []);
      
    return <Stack horizontal className={scrollClass.detailsListWrapper}>
    <Stack.Item grow styles={{root: {position:"relative", height:"100%"}}}>
        <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>                      
            <ShimmeredDetailsList
              {...props}
              items={filteredItems as any[]}
              columns={props.columns ? applyToColumns(props.columns) : undefined}
              groups={groups}
              onShouldVirtualize={() => false }
              selection={selection}
              onRenderRow={_onRenderRow?.bind(this)}
              layoutMode={DetailsListLayoutMode.fixedColumns}
              constrainMode={ConstrainMode.unconstrained}
              enableShimmer={props.items === undefined}
              onRenderDetailsHeader={_renderFixedDetailsHeader}
              setKey="set"
            />
        </ScrollablePane>
    </Stack.Item>
    {filterMenuProps && <ColumnFilterMenu {...filterMenuProps} />}
  </Stack >
}

export default FreezableDetailsList;